import React, { useState } from "react";

import AddIcon from "@mui/icons-material/Add";
import { Box, Fab, Grid, List, TextField, Typography } from "@mui/material";
import { intersection } from "lodash";
import { observer } from "mobx-react-lite";
import { makeStyles, withStyles } from "tss-react/mui";

import AllergenIngredientModal from "@/components/AllergenModal/AllergenIngredientModal";
import FoodSelect from "@/components/FoodSelect";
import { AllergiesKind, FoodItemFragment, FoodPortion } from "@/gql/graphql";
import { colors } from "@/theme/overrides/colors";

import IngredientsListItem from "./IngredientListItem";
import { Ingredient } from "../store/Ingredient";
import { useRecipe } from "../store/RecipeStore";

const StyledTextField = withStyles(TextField, {
  root: {
    "& .MuiOutlinedInput-root": {
      backgroundColor: colors.white,
      "& input::-webkit-clear-button ": {
        display: "none",
      },
      "& input::-webkit-outer-spin-button": {
        display: "none",
      },
      "& input::-webkit-inner-spin-button": {
        display: "none",
      },
    },
  },
});

const useStyles = makeStyles()({
  list: {
    flex: 1,
    height: "40vh",
    overflowY: "auto",
    overflowX: "hidden",
  },
});

const IngredientsForm: React.FC = () => {
  const recipe = useRecipe();
  const [ingredient] = useState(new Ingredient());
  const { classes } = useStyles();
  const urlParams = new URLSearchParams(window.location.search);
  const queryParams = urlParams.get("allergies") ?? "";
  const clientAllergies = queryParams.length
    ? (queryParams.split(",") as AllergiesKind[])
    : [];

  const isAddButtonDisabled =
    !ingredient.hasIngredient ||
    !ingredient.hasAmount ||
    !ingredient.hasPortionSize;

  const handleUpdateIngredient = (
    existingIngredient: Ingredient | undefined
  ) => {
    if (!existingIngredient) return;

    const updatedIngredient = existingIngredient;
    const foodPortionId = existingIngredient.portionSize?.id;
    const isSamePortionSizeId = foodPortionId === ingredient.portionSizeId;

    if (isSamePortionSizeId) {
      updatedIngredient.setQuantity(
        existingIngredient.quantity + ingredient.quantity
      );
      return recipe.updateIngredient(updatedIngredient);
    }

    updatedIngredient.setPortionSize(existingIngredient.portionSize?.id ?? "");
    updatedIngredient.setQuantity(ingredient.quantity);

    recipe.addIngredient();
  };

  const handleAddIngredient = () => {
    if (!ingredient.hasIngredient) return;

    const existingIngredient = recipe.ingredients.find(
      (item) =>
        item.ingredient?.id === ingredient.ingredient?.id &&
        item.portionSize?.id === ingredient.portionSize?.id
    );

    !existingIngredient
      ? recipe.addIngredient()
      : handleUpdateIngredient(existingIngredient);

    ingredient.reset();

    recipe.removeSelectedIngredient();
  };

  const handleValidateAddIngredient = () => {
    const hasAllergies =
      intersection(ingredient.ingredient?.allergies, clientAllergies).length !==
      0;

    hasAllergies ? ingredient.showAllergenModal(true) : handleAddIngredient();
  };

  return (
    <Box>
      <Typography fontSize={20} fontWeight={700}>
        Ingredients
      </Typography>
      <Box paddingBottom="16px" />
      <Grid container spacing={2}>
        <Grid item xs zeroMinWidth>
          <Typography fontWeight={600} fontSize={14} color={colors.grey}>
            Ingredient
          </Typography>
          <Box marginBottom={3} />
          <FoodSelect
            placeholder="Select ingredient"
            value={ingredient.ingredient as FoodItemFragment}
            onChange={(food) => ingredient.setIngredient(food)}
          />
        </Grid>
        <Grid item xs={2}>
          <Typography fontWeight={600} fontSize={14} color={colors.grey}>
            Qty
          </Typography>
          <Box marginBottom={3} />
          <StyledTextField
            type="number"
            size="small"
            fullWidth
            name="amount"
            placeholder="0"
            value={ingredient.hasAmount ? ingredient.quantity : ""}
            onChange={(evt) => ingredient.setQuantity(+evt.target.value)}
          />
        </Grid>
        <Grid item xs={3}>
          <Typography fontWeight={600} fontSize={14} color={colors.grey}>
            Measurement
          </Typography>
          <Box marginBottom={3} />
          <StyledTextField
            select
            name="portionIndex"
            size="small"
            fullWidth
            value={ingredient.portionSizeId}
            onChange={(evt) => {
              ingredient.setPortionSize(evt.target.value);
              recipe.setSelectedIngredient(ingredient);
            }}
            SelectProps={{ native: true }}
          >
            <option disabled value="">
              None
            </option>
            {ingredient.ingredient?.portions.map((portion) => {
              const portionSize = portion as FoodPortion;
              return (
                <option key={portionSize.id} value={portionSize.id}>
                  {portionSize.unit}
                </option>
              );
            })}
          </StyledTextField>
        </Grid>
        <Grid item>
          <Box marginTop={7}>
            <Fab
              color="primary"
              size="small"
              disabled={isAddButtonDisabled}
              onClick={handleValidateAddIngredient}
            >
              <AddIcon />
            </Fab>
          </Box>
        </Grid>
      </Grid>
      <List className={classes.list} disablePadding>
        {recipe.ingredients.map((ingredient, index) => (
          <IngredientsListItem
            key={ingredient.ingredient?.id}
            ingredient={ingredient}
            onRemoveIngredient={() => recipe.removeIngredient(index)}
          />
        ))}
      </List>
      <AllergenIngredientModal
        foodId={ingredient.ingredient?.id ?? ""}
        visibility={ingredient.isIngredientAllergenModalShown}
        onAddIngredient={() => {
          ingredient.showAllergenModal(false);
          handleAddIngredient();
        }}
        onClose={() => ingredient.showAllergenModal(false)}
      />
    </Box>
  );
};

export default observer(IngredientsForm);
